import React from 'react';
import { Database, Plug, HeartHandshake, Compass, ShieldCheck } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, Stagger, StaggerItem, SectionHead, CtaGhost, PageHero, Check, RuleLabel,
  TextLink, BigStat, ClosingCta,
} from '../components/V2Shared';
import { GO_LIVE_WEEKS, MIGRATION_FACTS } from '../data/site';

const FACT_ICONS = [Plug, HeartHandshake, Compass, ShieldCheck];

const FAQS = [
  {
    q: 'Do we have to leave our current PMS?',
    a: 'No. There are two ways to run Asha: make it your PMS with a full white-glove migration, or keep Dentrix, Eaglesoft, or Open Dental and run the AI workforce on top through a live integration. Most practices that start integrated migrate later — once they\'ve seen the OS work.',
  },
  {
    q: 'What happens to our phones?',
    a: 'The Communication Suite replaces your legacy VoIP — that\'s what lets Amy answer calls and lets every conversation land on the patient timeline. We ship and provision the hardware during Week 1, and your numbers port over with no downtime.',
  },
  {
    q: 'Will our operations pause during the cutover?',
    a: 'No. The rollout is phased — front office first, back office once you\'re comfortable — with parallel running through the transition and rollback procedures at every step. Your schedule never stops.',
  },
  {
    q: 'How much work is this for our team?',
    a: 'Week 1 is mostly ours: migration, configuration, and training the AI on your workflows. Your team\'s part is a handful of working sessions — appointment types, provider preferences, scheduling rules — plus staff training in Week 2.',
  },
  {
    q: 'What does support look like after go-live?',
    a: 'Twice-weekly syncs through your first eight weeks, then an ongoing named account team with DSO operating experience. They review your numbers with you and suggest the next play — strategic advisors, not a ticket queue.',
  },
];

const V2Switch = () => (
  <V2Layout title="Switching to Asha — White-Glove Onboarding">
    <PageHero
      eyebrow="Switching / white-glove onboarding"
      title={<>Live in four weeks. <span className="text-[#1a46e5]">Calm the whole way.</span></>}
      body="Switching software is the scariest sentence in dentistry — we built the process to make it boring. A dedicated implementation team migrates your data, configures your workflows, trains your staff, and phases the AI in step by step. Your operations never pause."
    >
      <CtaGhost to="/book-demo">Talk through your migration</CtaGhost>
      <TextLink to="/v2/why-asha">Why practices switch</TextLink>
    </PageHero>

    {/* two paths */}
    <section className="px-6 pb-12">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="01 / two ways to run asha" />
        <Stagger className="mt-10 grid gap-5 lg:grid-cols-2">
          {[
            {
              icon: Database,
              tag: 'path a',
              title: 'Asha as your PMS',
              body: 'Full migration from your current system. Charts, ledgers, schedules, documents, and imaging move into Asha — and the whole operating system, clinical AI included, runs as one.',
              points: ['Complete data migration, validated in a sandbox first', 'Every module and every agent fully native', 'The deepest automation — one data model end to end'],
            },
            {
              icon: Plug,
              tag: 'path b',
              title: 'Keep your PMS, add the workforce',
              body: 'Keep Dentrix, Eaglesoft, or Open Dental. Asha connects through a live integration and the AI workforce runs on top — no migration required to start.',
              points: ['Live two-way sync with your existing system', 'Front office and revenue cycle agents from day one', 'Migrate later if you want to — many practices do'],
            },
          ].map((p) => (
            <StaggerItem key={p.title} className="h-full">
              <div className="v2-card h-full rounded-2xl p-8">
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eef3ff]">
                    <p.icon className="h-5 w-5 text-[#1a46e5]" />
                  </span>
                  <span className="v2-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#a8bcf0]">{p.tag}</span>
                </div>
                <div className="v2-display mt-5 text-[24px] font-semibold leading-tight text-[#0a1633]">{p.title}</div>
                <p className="mt-3 text-[15px] leading-relaxed text-[#3f4d6e]">{p.body}</p>
                <ul className="mt-5 space-y-2.5 border-t border-[#eef3ff] pt-5">
                  {p.points.map((pt) => (
                    <Check key={pt}>{pt}</Check>
                  ))}
                </ul>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* 4-week timeline */}
    <section className="px-6 py-20 md:py-24">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="02 / the four weeks" />
        <div className="mt-12">
          <SectionHead
            eyebrow="Go-live timeline"
            title={<>Front office in two weeks. Everything in four.</>}
            body="Phased on purpose: the visible wins land first, and the back office switches on once your team trusts what they're seeing."
          />
        </div>
        <div className="mt-14 space-y-0">
          {GO_LIVE_WEEKS.map((w, i) => (
            <FadeUp key={w.week} delay={i * 0.05}>
              <div className="grid gap-6 border-t border-[#dde5f3] py-10 md:grid-cols-[160px_1fr_1fr] md:gap-12 last:border-b">
                <div>
                  <div className="v2-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1a46e5]">{w.week}</div>
                  <div className="v2-display mt-2 text-[40px] font-semibold leading-none text-[#dde5f3] md:text-[56px]">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                </div>
                <div>
                  <div className="v2-display text-[24px] font-semibold leading-tight text-[#0a1633]">{w.title}</div>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#3f4d6e]">{w.body}</p>
                </div>
                <ul className="space-y-2.5 md:pt-1">
                  {w.items.map((item) => (
                    <Check key={item}>{item}</Check>
                  ))}
                </ul>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* white glove panel */}
    <section className="px-6 pb-24">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <SectionHead
            dark
            eyebrow="03 / white glove, defined"
            title={<>You get an implementation team, not a help center.</>}
            body="Setup, migration, and training are done with you, live — and the same people stay on your account as strategic advisors long after go-live."
          />
          <Stagger className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {MIGRATION_FACTS.map((f, i) => {
              const Icon = FACT_ICONS[i];
              return (
                <StaggerItem key={f.title} className="h-full">
                  <div className="h-full rounded-2xl border border-white/12 bg-white/5 p-7">
                    <Icon className="h-5 w-5 text-[#9db4ff]" />
                    <div className="v2-display mt-4 text-[19px] font-semibold leading-tight text-white">{f.title}</div>
                    <p className="mt-2.5 text-[14px] leading-relaxed text-white/70">{f.body}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
          <FadeUp delay={0.15}>
            <div className="mt-16 grid grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="4" label="weeks to full go-live" />
              <BigStat dark value="2×" label="weekly syncs, 8 weeks" />
              <BigStat dark value="1" label="named account team" />
              <BigStat dark value="0" label="days of paused operations" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="px-6 pb-24 md:pb-28">
      <div className="mx-auto max-w-4xl">
        <RuleLabel label="04 / the questions everyone asks" />
        <div className="mt-12 space-y-0">
          {FAQS.map((f, i) => (
            <FadeUp key={f.q} delay={i * 0.04}>
              <details className="group border-t border-[#dde5f3] py-6 last:border-b">
                <summary className="flex cursor-pointer list-none items-baseline gap-5 [&::-webkit-details-marker]:hidden">
                  <span className="v2-mono shrink-0 text-[11px] font-medium text-[#a8bcf0]">{String(i + 1).padStart(2, '0')}</span>
                  <span className="v2-display text-[20px] font-semibold leading-snug text-[#0a1633] transition-colors group-hover:text-[#1a46e5] md:text-[22px]">
                    {f.q}
                  </span>
                  <span aria-hidden className="v2-mono ml-auto shrink-0 text-[18px] text-[#a8bcf0] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 pl-10 text-[15px] leading-relaxed text-[#3f4d6e]">{f.a}</p>
              </details>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <ClosingCta
      title="Tell us what you're running today."
      body="Bring your current stack to a 30-minute call and we'll map your exact migration path — what moves, what connects, and what week one looks like."
    />
  </V2Layout>
);

export default V2Switch;
